import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, BriefcaseBusiness, ChevronLeft, ChevronRight,
  Code2, Download, ExternalLink, Github, Linkedin, Mail, MapPin,
  Menu, Play, Smartphone, Warehouse
} from "lucide-react";
import { Card, CardContent } from "./components/ui/Card";
import { Button } from "./components/ui/Button";
import ProjectMediaPlaceholder from "./components/ProjectMediaPlaceholder";
import { otherProjects, professionalProjects, projects, skillGroups } from "./portfolioData";

const contactEmail = "mohamedsallah176@gmail.com";
const cvAssetPath = "/Mohamad-Sallah-CV.pdf";

function useRevealOnce() {
  useEffect(() => {
    const elements = document.querySelectorAll("[data-reveal]");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8%", threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
}

const ImageCarousel = React.memo(function ImageCarousel({ images = [], projectTitle, priority = false }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (!images.length) return null;
  const showNext = () => { setFailed(false); setLoaded(false); setCurrentImage((value) => (value + 1) % images.length); };
  const showPrevious = () => { setFailed(false); setLoaded(false); setCurrentImage((value) => (value - 1 + images.length) % images.length); };

  return (
    <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-slate-700 bg-slate-950/70">
      {!failed ? (
        <img
          src={images[currentImage]}
          alt={`${projectTitle} product screenshot ${currentImage + 1}`}
          width="960"
          height="600"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`media-image h-full w-full object-contain ${loaded ? "is-loaded" : ""}`}
        />
      ) : (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-400">
          <ProjectMediaPlaceholder project={{ title: projectTitle, type: "mobile", eyebrow: "Product interface" }} compact />
        </div>
      )}
      {images.length > 1 && (
        <>
          <button type="button" onClick={showPrevious} aria-label={`Previous ${projectTitle} screenshot`} className="carousel-control left-3">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" onClick={showNext} aria-label={`Next ${projectTitle} screenshot`} className="carousel-control right-3">
            <ChevronRight className="h-5 w-5" />
          </button>
          <span className="absolute bottom-3 right-3 rounded-md bg-slate-950/80 px-2 py-1 text-xs text-slate-300">
            {currentImage + 1}/{images.length}
          </span>
        </>
      )}
    </div>
  );
});

function SectionHeading({ eyebrow, title, description, signature = false }) {
  return (
    <div className={`mb-8 max-w-3xl sm:mb-12 ${signature ? "signature-accent" : ""}`} data-reveal data-reveal-kind="heading">
      <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-cyan-400">{eyebrow}</p>
      <h2 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">{title}</h2>
      {description && <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">{description}</p>}
    </div>
  );
}

function ProjectIcon({ type }) {
  if (type === "mobile") return <Smartphone className="h-5 w-5" />;
  if (type === "dataset") return <Warehouse className="h-5 w-5" />;
  return <Code2 className="h-5 w-5" />;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const projectsRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  useRevealOnce();

  const scrollToProjects = () => projectsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="min-h-screen overflow-x-hidden bg-slate-950 text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(8,145,178,0.12),transparent_32%),radial-gradient(circle_at_82%_28%,rgba(37,99,235,0.10),transparent_30%)]" />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/90 bg-slate-950/90 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6" aria-label="Main navigation">
          <a href="#top" className="text-lg font-bold tracking-tight text-white">Mohamad <span className="text-cyan-400">Sallah</span></a>
          <div className="hidden items-center gap-7 text-sm text-slate-300 sm:flex">
            <a className="nav-link" href="#experience">Experience</a>
            <a className="nav-link" href="#projects">Projects</a>
            <a className="nav-link" href="#skills">Skills</a>
            <a className="nav-link" href="#contact">Contact</a>
          </div>
          <button type="button" className="rounded-lg p-2 text-cyan-300 sm:hidden" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
            <Menu className="h-6 w-6" />
          </button>
        </nav>
        {menuOpen && (
          <div className="border-t border-slate-800 bg-slate-950 px-4 py-4 sm:hidden">
            {["experience", "projects", "skills", "contact"].map((item) => <a key={item} href={`#${item}`} onClick={() => setMenuOpen(false)} className="block py-2 capitalize text-slate-200">{item}</a>)}
          </div>
        )}
      </header>

      <section id="top" className="relative flex min-h-screen items-center px-4 pb-16 pt-28 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.9fr)] lg:gap-12 xl:gap-16">
          <div className="hero-copy max-w-4xl">
            <div className="hero-step mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/5 px-4 py-2 text-sm text-cyan-200">
              <MapPin className="h-4 w-4" /> Dubai, UAE
            </div>
            <p className="hero-step hero-signature mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">Mohamad Sallah</p>
            <h1 className="hero-step max-w-4xl text-4xl font-bold leading-[1.08] tracking-[-0.035em] text-white sm:text-6xl lg:text-7xl">
              AI Application &amp; Software Engineer
            </h1>
            <p className="hero-step mt-6 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              I build grounded AI applications and ship production mobile, backend, and business systems—including real-time, offline-first, and hardware-integrated software.
            </p>
            <p className="hero-step mt-5 text-sm font-medium text-slate-300 sm:text-base">AI Engineering • Mobile • Backend • Production Systems</p>
            <p className="hero-step mt-3 text-sm font-medium text-slate-400 sm:text-base">Flutter • Kotlin • Laravel • FastAPI • RAG/GraphRAG • LLM Applications</p>
            <div className="hero-step mt-9 flex flex-col gap-3 sm:flex-row">
              <Button onClick={scrollToProjects} className="primary-action px-6 py-3">View Projects <ArrowRight className="ml-2 h-4 w-4" /></Button>
              {cvAssetPath ? (
                <a href={cvAssetPath} target="_blank" rel="noreferrer" className="secondary-action"><Download className="mr-2 h-4 w-4" />Download CV</a>
              ) : (
                <a href={`mailto:${contactEmail}?subject=CV Request — Mohamad Sallah`} className="secondary-action"><Download className="mr-2 h-4 w-4" />Request CV</a>
              )}
            </div>
            <div className="hero-step mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-400">
              <a className="social-link" href="https://www.linkedin.com/in/mohamad-sallah-aa3184326/" target="_blank" rel="noreferrer"><Linkedin className="h-4 w-4" />LinkedIn</a>
              <a className="social-link" href="https://github.com/MoSallah21" target="_blank" rel="noreferrer"><Github className="h-4 w-4" />GitHub</a>
              <a className="social-link" href={`mailto:${contactEmail}`}><Mail className="h-4 w-4" />Contact</a>
            </div>
          </div>
          <aside className="hero-evidence hidden overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/45 lg:grid" aria-label="Current engineering work" data-hero-panel>
            <div className="hero-portrait-frame">
              <img
                src="/images/mohamad-sallah-profile.jpg"
                alt="Mohamad Sallah, AI Application and Software Engineer"
                width="1254"
                height="1254"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="hero-portrait"
              />
            </div>
            <div className="p-5 xl:p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Currently building</p>
              <div className="mt-5 border-b border-slate-800 pb-5">
                <p className="text-2xl font-bold tracking-tight text-white">SpreadX</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">Production POS &amp; Business Platform</p>
                <p className="mt-1 text-sm text-cyan-400">Dubai, UAE</p>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {["Offline-first systems", "POS & hardware integrations", "Multi-branch operations"].map((signal) => (
                  <li key={signal} className="flex items-center gap-3"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />{signal}</li>
                ))}
              </ul>
              <p className="mt-6 border-t border-slate-800 pt-4 text-xs font-medium tracking-wide text-slate-500">Flutter • Kotlin • REST APIs</p>
            </div>
          </aside>
          </div>
        </div>
      </section>

      <section id="experience" className="section-shell border-y border-slate-800/80 bg-slate-900/30">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="Professional experience" title="Production ownership, not just project work" description="Experience building operational software across UAE retail, mobility, ERP, warehouse, and accounting domains." />
          <div className="grid gap-5 lg:grid-cols-2">
            <article className="surface-card" data-reveal>
              <div className="mb-5 flex items-start justify-between gap-4"><div><h3 className="text-xl font-bold">SpreadX.ae</h3><p className="mt-1 text-cyan-300">Mobile Software Engineer</p></div><span className="text-right text-sm text-slate-400">Dubai, UAE<br />Oct 2025 – Present</span></div>
              <p className="text-sm leading-7 text-slate-300">Building a production multi-branch POS and business platform across Flutter and native Android. Work spans sales, inventory, invoicing, kitchen and fulfillment, warehouse, offline synchronization, REST APIs, Firebase, WebSockets, printers, scanners, POS hardware, production debugging, and support.</p>
              <p className="mt-4 text-sm text-slate-400">AI-assisted implementation and debugging with continued ownership of production quality and delivery.</p>
              <div className="mt-5 flex flex-wrap gap-2"><span className="context-label">UAE Retail</span><span className="context-label">Offline-first</span><span className="context-label">POS Hardware</span></div>
            </article>
            <article className="surface-card" data-reveal>
              <div className="mb-5 flex items-start justify-between gap-4"><div><h3 className="text-xl font-bold">Paradox Team</h3><p className="mt-1 text-cyan-300">Mobile Engineer</p></div><span className="text-right text-sm text-slate-400">Jul 2022 – Aug 2025</span></div>
              <p className="text-sm leading-7 text-slate-300">Delivered production applications across ride-hailing, ERP, warehouse, and accounting systems, including real-time workflows and backend integrations. Held technical and delivery ownership while leading a small development team.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="section-shell border-b border-slate-800/80 bg-slate-900/20">
        <div className="mx-auto max-w-6xl">
          <div className="ask-teaser" data-reveal>
            <div className="max-w-2xl"><p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-400">Grounded portfolio assistant</p><h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Ask Mohamad AI</h2><p className="mt-4 leading-7 text-slate-300">Explore my experience, projects, and engineering work through an AI-powered assistant grounded in this portfolio.</p></div>
            <div className="mt-7 flex flex-wrap gap-2">{["What am I working on now?", "What Flutter experience do I have?", "What AI systems have I built?"].map((question) => <button key={question} type="button" className="ask-chip" onClick={() => navigate("/ask")}>{question}</button>)}</div>
            <Button onClick={() => navigate("/ask")} className="primary-action mt-7 px-5 py-3">Ask about my work <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
      </section>

      <section id="projects" ref={projectsRef} className="section-shell scroll-mt-20">
        <div className="mx-auto max-w-6xl">
          <SectionHeading signature eyebrow="Selected engineering work" title="Production systems and end-to-end products" description="Six projects selected for operational complexity, engineering scope, and relevance to mobile, backend, business systems, and AI application roles." />
          <div className="space-y-6">
            {projects.map((project, index) => (
              <Card key={project.id} data-reveal className={`project-card ${project.featured ? "flagship-card" : ""}`}>
                <CardContent className="grid gap-7 p-5 sm:p-7 lg:grid-cols-[0.95fr_1.05fr] lg:p-9">
                  <div className="flex flex-col justify-between">
                    <div>
                      <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-500/25 bg-cyan-500/10 text-cyan-300"><ProjectIcon type={project.type} /></div>
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-400">{project.eyebrow}</p>
                      <h3 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{project.title}</h3>
                      <p className="mt-4 leading-7 text-slate-300">{project.description}</p>
                      <div className="mt-6 flex flex-wrap gap-2">{project.technologies.slice(0, 7).map((tech) => <span key={tech} className="tech-tag">{tech}</span>)}</div>
                    </div>
                    <div className="mt-7 flex flex-wrap gap-3">
                      <Button onClick={() => navigate(`/project/${project.id}`, { state: { project } })} className="primary-action px-5 py-2.5">View Case Study <ArrowRight className="ml-2 h-4 w-4" /></Button>
                      {project.links?.github && <a className="icon-action" href={project.links.github} target="_blank" rel="noreferrer" aria-label={`${project.title} GitHub`}><Github className="h-5 w-5" /></a>}
                      {project.links?.video && <a className="icon-action" href={project.links.video} target="_blank" rel="noreferrer" aria-label={`${project.title} video`}><Play className="h-5 w-5" /></a>}
                    </div>
                  </div>
                  {(project.homepageCoverImage || project.coverImage) ? <ImageCarousel images={[project.homepageCoverImage || project.coverImage]} projectTitle={project.title} priority={index === 0} /> : <div className="aspect-[16/10] overflow-hidden rounded-xl border border-slate-800"><ProjectMediaPlaceholder project={project} /></div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell border-y border-slate-800/80 bg-slate-900/30">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="Additional professional & technical evidence" title="Other relevant projects" />
          <div className="mb-8 grid gap-4 md:grid-cols-2">{professionalProjects.map((project) => <article key={project.id} className="surface-card" data-reveal><ProjectIcon type={project.type} /><p className="mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-400">{project.eyebrow}</p><h3 className="mt-2 text-lg font-bold">{project.title}</h3><p className="mt-3 text-sm leading-6 text-slate-400">{project.description}</p><button type="button" className="mt-5 inline-flex items-center text-sm font-medium text-cyan-300 hover:text-cyan-200" onClick={() => navigate(`/project/${project.id}`, { state: { project } })}>View Case Study <ArrowRight className="ml-2 h-4 w-4" /></button></article>)}</div>
          <div className="grid gap-4 md:grid-cols-3">{otherProjects.map((project) => <article key={project.id} className="surface-card" data-reveal><ProjectIcon type={project.type} /><h3 className="mt-5 text-lg font-bold">{project.title}</h3><p className="mt-3 text-sm leading-6 text-slate-400">{project.description}</p><a className="mt-5 inline-flex items-center text-sm font-medium text-cyan-300 hover:text-cyan-200" href={project.link} target="_blank" rel="noreferrer">View evidence <ExternalLink className="ml-2 h-4 w-4" /></a></article>)}</div>
        </div>
      </section>

      <section id="skills" className="section-shell scroll-mt-20">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="Engineering capabilities" title="Skills organized around delivery" description="Tools support the work; architecture, integration, debugging, and production ownership define it." />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{skillGroups.map((group) => <article key={group.title} className="surface-card" data-reveal><h3 className="flex items-center gap-3 font-bold text-white"><BriefcaseBusiness className="h-5 w-5 text-cyan-400" />{group.title}</h3><ul className="mt-5 space-y-2 text-sm text-slate-300">{group.items.map((item) => <li key={item} className="flex gap-3"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />{item}</li>)}</ul></article>)}</div>
        </div>
      </section>

      <section id="contact" className="section-shell border-t border-slate-800/80 bg-slate-900/30 text-center">
        <div className="mx-auto max-w-3xl" data-reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-400">Dubai, UAE</p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-5xl">Open to engineering and product opportunities in the UAE</h2>
          <p className="mx-auto mt-5 max-w-2xl leading-7 text-slate-300">Available for software engineering, mobile, AI application, and product-building conversations.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row"><a className="primary-action" href={`mailto:${contactEmail}`}><Mail className="mr-2 h-4 w-4" />Email Mohamad</a><a className="secondary-action" href="https://www.linkedin.com/in/mohamad-sallah-aa3184326/" target="_blank" rel="noreferrer"><Linkedin className="mr-2 h-4 w-4" />LinkedIn</a></div>
        </div>
      </section>

      <footer className="border-t border-slate-800 px-4 py-7 text-center text-sm text-slate-500">© {new Date().getFullYear()} Mohamad Sallah. AI Application &amp; Software Engineer — Dubai, UAE.</footer>
    </main>
  );
}

export { projects } from "./portfolioData";
export { trainingProjects, aiProjects, professionalProjects } from "./portfolioData";
